import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import LoadingSpinner from "../../Components/LoadingSpinner";
import UnPaidInvoice from "./UnPaidInvoice";
import { DateBox } from "devextreme-react";
import { useInvoices } from "../../../Providers/InvoicesProvider";
import InformationErrorModal from "../../Components/Modals/InformationErrorModal";
import { useParams } from "../../../Providers/ParamsProvider";

const UnPaidInvoices = ({ invoices, isLoading, currentView, setShowNoEInvoiceModal }) => {
    const { t } = useTranslation();
    const { handleGetUnPaid } = useInvoices()
    const [selectedSeller, setSelectedSeller] = useState('');
    const [selectedDocType, setSelectedDocType] = useState('');
    const [dateFrom, setDateFrom] = useState('')
    const [dateTo, setDateTo] = useState('')
    const dateFromRef = useRef(null)
    const dateToRef = useRef(null)
    const [showError, setShowError] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [selectedOrder, setSelectedOrder] = useState('');
    const { isMobile } = useParams();

    // Prefiksy dla różnych typów dokumentów
    const invoicePrefixes = ['F', 'FV', 'S', 'SD', 'E', 'EU', 'EP'];
    const correctionPrefixes = ['V', 'W', 'WD', 'N', 'NN', 'EC', 'EK', 'EV'];

    // Funkcja do filtrowania listy dokumentów    
    const filteredInvoices = invoices && invoices.length > 0 ? invoices.filter(invoice => {
        const matchesSeller = selectedSeller ? invoice.seller === selectedSeller : true;

        const matchesDocType = (() => {
            if (!selectedDocType || selectedDocType === 'Wszystkie') return true; // Wszystkie
            if (selectedDocType === 'Faktury') {
                return invoicePrefixes.some(prefix => invoice.docNumber.startsWith(prefix));
            }
            if (selectedDocType === 'Korekty') {
                return correctionPrefixes.some(prefix => invoice.docNumber.startsWith(prefix));
            }
            return false;
        })();
        return matchesSeller && matchesDocType;
    }) : [];

    //Funkcja do sortowania listy dokumentów
    const sortedInvoices = [...filteredInvoices].sort((a, b) => {
        switch (selectedOrder) {
            case "newest":
                return new Date(b.docCreated) - new Date(a.docCreated); // Sortowanie od najnowszych
            case "oldest":
                return new Date(a.docCreated) - new Date(b.docCreated); // Sortowanie od najstarszych
            case "docAsc":
                return a.docNumber.localeCompare(b.docNumber); // Numer dokumentu rosnąco
            case "docDesc":
                return b.docNumber.localeCompare(a.docNumber); // Numer dokumentu malejąco
            case "valueAsc":
                return a.value - b.value; // Wartość dokumentu rosnąco
            case "valueDesc":
                return b.value - a.value; // Wartość dokumentu malejąco
            case "paidAsc":
                return a.paidValue - b.paidValue; // Wpłacono rosnąco
            case "paidDesc":
                return b.paidValue - a.paidValue; // Wpłacono malejąco
            case "timeToPayDesc":
                return new Date(b.dateToPay) - new Date(a.dateToPay); // Termin płątności rosnąco
            case "timeToPayAsc":
                return new Date(a.dateToPay) - new Date(b.dateToPay); // Termin płatności malejąco
            default:
                return 0;
        }
    });

    const DateFromOnValueChanged = (e) => {
        if (e.value) {
            const selectedDate = e.value.toLocaleString('en-CA').substring(0, 10);

            if (dateTo.length === 0) {
                setDateFrom(selectedDate)
            } else if (selectedDate > dateTo) {
                setErrorMessage('Data OD nie może być poźniejsza niż data DO!')
                setShowError(true)
                if (dateFromRef.current) {
                    dateFromRef.current.instance.reset();
                }
                setDateFrom('')
            } else {
                setDateFrom(selectedDate)
            }
        }
    }

    const DateToOnValueChanged = (e) => {
        if (e.value) {
            const selectedDate = e.value.toLocaleString('en-CA').substring(0, 10);

            if (dateFrom.length === 0) {
                setDateTo(selectedDate)
            } else if (selectedDate < dateFrom) {
                setErrorMessage('Data DO nie może być wcześniejsza niż data OD!')
                setShowError(true)
                if (dateToRef.current) {
                    dateToRef.current.instance.reset();
                }
                setDateTo('')
            } else {
                setDateTo(selectedDate)
            }
        }
    }

    useEffect(() => {
        setSelectedDocType('')
        setSelectedSeller('')
        setSelectedOrder('timeToPayAsc')
    }, [currentView])

    useEffect(() => {
        handleGetUnPaid(dateFrom, dateTo);
    }, [dateFrom, dateTo])

    const clearFilters = () => {
        setSelectedDocType('');
        setSelectedSeller('');
        setDateFrom('');
        setDateTo('');
        setSelectedOrder('timeToPayAsc')

        if (dateFromRef.current) {
            dateFromRef.current.instance.reset();
        }
        if (dateToRef.current) {
            dateToRef.current.instance.reset();
        }
    };
    // Pobranie unikalnych wartości do wyboru w filtrach
    const sellers = invoices && invoices.length > 0 ? [...new Set(invoices.map(invoice => invoice.seller).filter(seller => seller))] : []

    return (
        <div>
            <div className="row justify-content-center">
                <div className="justify-content-center">
                    {/* Sekcja filtrów */}
                    <div className="col-12 col-xxl-11">
                        <InformationErrorModal show={showError} setShow={setShowError} title={'Błędna data'} message={errorMessage} isError={false} />
                        {isMobile ?
                            <div className="row">
                                <div className='col-6 date-filter'>
                                    <label style={{ fontSize: '14px', fontWeight: 'bold' }} htmlFor="investment">Od</label>
                                    <DateBox acceptCustomValue={false} adaptivityEnabled={true} onValueChanged={DateFromOnValueChanged} ref={dateFromRef} />
                                </div>
                                <div className="col-6 date-filter">
                                    <label style={{ fontSize: '14px', fontWeight: 'bold' }} htmlFor="investment">Do</label>
                                    <DateBox acceptCustomValue={false} adaptivityEnabled={true} onValueChanged={DateToOnValueChanged} ref={dateToRef} />
                                </div>
                                <div className="filters col-12 d-grid">
                                    <div>
                                        <label style={{ fontSize: '14px' }} htmlFor="docType">Rodzaj dokumentu</label>
                                        <select
                                            style={{ fontSize: '14px' }}
                                            id="docType"
                                            value={selectedDocType}
                                            onChange={(e) => setSelectedDocType(e.target.value)}
                                        >
                                            <option value="">Wszystkie</option>
                                            <option value="Faktury">Faktury</option>
                                            <option value="Korekty">Korekty</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label style={{ fontSize: '14px' }} htmlFor="seller">Sprzedający</label>
                                        <select
                                            style={{ fontSize: '14px' }}
                                            id="seller"
                                            value={selectedSeller}
                                            onChange={(e) => setSelectedSeller(e.target.value)}
                                        >
                                            <option value="">Wszyscy sprzedający</option>
                                            {sellers.map((seller, index) => (
                                                <option key={index} value={seller}>{seller}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label style={{ fontSize: '14px' }} htmlFor="seller">Sortuj według</label>
                                        <select
                                            style={{ fontSize: '14px' }}
                                            id="order"
                                            value={selectedOrder}
                                            onChange={(e) => setSelectedOrder(e.target.value)}
                                        >
                                            <option value="timeToPayAsc">Termin rosnąco</option>
                                            <option value="timeToPayDesc">Termin malejąco</option>
                                            <option value="newest">Data dok. malejąco</option>
                                            <option value="oldest">Data dok. rosnąco</option>
                                            <option value="docDesc">Nr dok. malejąco</option>
                                            <option value="docAsc">Nr dok. rosnąco</option>
                                            <option value="valueDesc">Wartość malejąco</option>
                                            <option value="valueAsc">Wartość rosnąco</option>
                                            <option value="paidDesc">Wpłacono malejąco</option>
                                            <option value="paidAsc">Wpłacono rosnąco</option>
                                        </select>

                                    </div>
                                    <button
                                        style={{ fontSize: '15px', height: '40px' }}
                                        onClick={() => {
                                            clearFilters()
                                        }}
                                        className='btn-app-primary'>
                                        X
                                    </button>
                                </div>
                            </div> :
                            <div className="row">
                                <div className=' col-2 date-filter'>
                                    <label style={{ fontSize: '14px', fontWeight: 'bold' }} htmlFor="investment">Od</label>
                                    <DateBox acceptCustomValue={false} adaptivityEnabled={true} onValueChanged={DateFromOnValueChanged} ref={dateFromRef} />
                                </div>
                                <div className="col-2 date-filter">
                                    <label style={{ fontSize: '14px', fontWeight: 'bold' }} htmlFor="investment">Do</label>
                                    <DateBox acceptCustomValue={false} adaptivityEnabled={true} onValueChanged={DateToOnValueChanged} ref={dateToRef} />
                                </div>
                                <div className="filters col-7 ">
                                    <div>
                                        <label style={{ fontSize: '14px' }} htmlFor="docType">Rodzaj dokumentu</label>
                                        <select
                                            style={{ fontSize: '14px' }}
                                            id="docType"
                                            value={selectedDocType}
                                            onChange={(e) => setSelectedDocType(e.target.value)}
                                        >
                                            <option value="">Wszystkie</option>
                                            <option value="Faktury">Faktury</option>
                                            <option value="Korekty">Korekty</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label style={{ fontSize: '14px' }} htmlFor="seller">Sprzedający</label>
                                        <select
                                            style={{ fontSize: '14px' }}
                                            id="seller"
                                            value={selectedSeller}
                                            onChange={(e) => setSelectedSeller(e.target.value)}
                                        >
                                            <option value="">Wszyscy sprzedający</option>
                                            {sellers.map((seller, index) => (
                                                <option key={index} value={seller}>{seller}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label style={{ fontSize: '14px' }} htmlFor="seller">Sortuj według</label>
                                        <select
                                            style={{ fontSize: '14px' }}
                                            id="order"
                                            value={selectedOrder}
                                            onChange={(e) => setSelectedOrder(e.target.value)}
                                        >
                                            <option value="timeToPayAsc">Termin rosnąco</option>
                                            <option value="timeToPayDesc">Termin malejąco</option>
                                            <option value="newest">Data dok. malejąco</option>
                                            <option value="oldest">Data dok. rosnąco</option>
                                            <option value="docDesc">Nr dok. malejąco</option>
                                            <option value="docAsc">Nr dok. rosnąco</option>
                                            <option value="valueDesc">Wartość malejąco</option>
                                            <option value="valueAsc">Wartość rosnąco</option>
                                            <option value="paidDesc">Wpłacono malejąco</option>
                                            <option value="paidAsc">Wpłacono rosnąco</option>
                                        </select>
                                    </div>
                                    <button
                                        style={{ fontSize: '15px', width: '40px', height: '40px' }}
                                        onClick={() => {
                                            clearFilters()
                                        }}
                                        className='btn-app-primary mt-4'>
                                        X
                                    </button>
                                </div>
                            </div>}
                    </div>
                    <div className="table-responsive">
                        <table className={`${isMobile ? 'table-font-small' : ''} table table-bordered`}>
                            <thead>
                                <tr>
                                    {/* <th style={{ alignContent: 'center' }}></th> */}
                                    <th style={{ alignContent: 'start', verticalAlign: 'top', width: '10%', fontSize: '14px' }}>{t("pay_term_table_header").toLocaleUpperCase()}</th>
                                    <th style={{ alignContent: "start", verticalAlign: 'top', width: "20%", fontSize: '14px' }}>
                                        {t("seller_table_header_orders_page").toLocaleUpperCase()}
                                    </th>
                                    <th style={{ alignContent: "start", verticalAlign: 'top', width: "15%", fontSize: '14px' }}>
                                        {t("nr_dok_table_header_unpaid_page").toLocaleUpperCase()}
                                    </th>
                                    <th style={{ alignContent: "start", verticalAlign: 'top', width: "10%", fontSize: '14px' }}>
                                        {t("date_dok_table_header_unpaid_page").toLocaleUpperCase()}
                                    </th>
                                    <th style={{ alignContent: "start", verticalAlign: 'top', width: "10%", fontSize: '14px', textAlign: 'right' }}>
                                        {t("value_table_header_unpaid_page").toLocaleUpperCase()}
                                    </th>
                                    <th style={{ alignContent: "start", verticalAlign: 'top', width: "10%", fontSize: '14px', textAlign: 'right' }}>
                                        {t("value_left_to_pay").toLocaleUpperCase()}
                                        <br />
                                        <span style={{ fontWeight: 'normal' }}>{t("value_paid").toLocaleUpperCase()}</span>
                                    </th>
                                    <th style={{ width: 'auto', verticalAlign: 'top', fontSize: '14px' }}>
                                        {t("comments").toLocaleUpperCase()}
                                    </th>
                                    <th style={{ width: '5%' }}></th>
                                </tr>
                            </thead>
                            <tbody>
                                {sortedInvoices &&
                                    sortedInvoices.map((invoice, index) => {
                                        return <UnPaidInvoice key={index} invoice={invoice} setShowNoEInvoiceModal={setShowNoEInvoiceModal} />;
                                    })}
                            </tbody>
                        </table>
                        {isLoading && <LoadingSpinner />}
                        {!isLoading && sortedInvoices && sortedInvoices.length === 0 && sortedInvoices && (
                            <div style={{ textAlign: "center" }}>
                                {t("message_norecords_table_invoices_page")}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UnPaidInvoices;
